// src/components/Replan/utils/transformationUtils.js

/**
 * Converts a shape's relative points to absolute canvas coordinates.
 * Shapes store points relative to shape.x/y anchor. This adds the anchor offset.
 * @param {Object} shape - Shape with { x, y, points }
 * @returns {Array<number>} Absolute points [x1, y1, x2, y2, ...]
 */
export const toAbsolutePoints = (shape) => {
    const ox = shape.x || 0;
    const oy = shape.y || 0;
    const pts = shape.points || [];
    const abs = [];
    for (let i = 0; i < pts.length; i += 2) {
        abs.push(pts[i] + ox, pts[i + 1] + oy);
    }
    return abs;
};

/**
 * Converts absolute canvas coordinates back to a shape's relative format,
 * setting shape.x/y to the first point and all points relative to it.
 * @param {Array<number>} absPoints - Absolute [x1, y1, x2, y2, ...]
 * @returns {{ x: number, y: number, points: Array<number> }}
 */
export const toRelativeShape = (absPoints) => {
    if (!absPoints || absPoints.length < 2) return { x: 0, y: 0, points: absPoints || [] };
    const anchorX = absPoints[0];
    const anchorY = absPoints[1];
    const rel = [];
    for (let i = 0; i < absPoints.length; i += 2) {
        rel.push(absPoints[i] - anchorX, absPoints[i + 1] - anchorY);
    }
    return { x: anchorX, y: anchorY, points: rel };
};

/**
 * Calculates the bounding box of a set of shapes using absolute coordinates.
 * @param {Array} shapes - The shapes to measure.
 * @returns {Object} An object with { minX, minY, maxX, maxY }.
 */
const getBoundingBox = (shapes) => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    shapes.forEach(shape => {
        const abs = toAbsolutePoints(shape);
        for (let i = 0; i < abs.length; i += 2) {
            minX = Math.min(minX, abs[i]);
            minY = Math.min(minY, abs[i + 1]);
            maxX = Math.max(maxX, abs[i]);
            maxY = Math.max(maxY, abs[i + 1]);
        }

        // Also consider shapes without points (RECTANGLE, CIRCLE, POINT)
        if ((!abs.length || abs.length < 2) && (shape.x !== undefined || shape.y !== undefined)) {
            const sx = shape.x || 0;
            const sy = shape.y || 0;
            minX = Math.min(minX, sx);
            minY = Math.min(minY, sy);
            maxX = Math.max(maxX, sx + (shape.width || 0));
            maxY = Math.max(maxY, sy + (shape.height || 0));
        }
    });

    return { minX, minY, maxX, maxY };
};

/**
 * Calculates the center of the bounding box that encloses a group of shapes.
 * Uses absolute canvas coordinates (shape.x + shape.y offsets are included).
 * @param {Array} shapes - The shapes to find the collective center of.
 * @returns {Object} An object with { x, y } representing the center of the group's bounding box.
 */
export const getShapesCenter = (shapes) => {
    if (!shapes || shapes.length === 0) {
        return { x: 0, y: 0 };
    }

    const { minX, minY, maxX, maxY } = getBoundingBox(shapes);

    if (minX === Infinity) {
        return { x: 0, y: 0 };
    }

    return {
        x: minX + (maxX - minX) / 2,
        y: minY + (maxY - minY) / 2,
    };
};

/**
 * Rotates a flat array of points [x1, y1, x2, y2, ...] around a center.
 * @param {Array<number>} points - The points to rotate.
 * @param {number} angle - The rotation angle in degrees.
 * @param {Object} center - The center of rotation with { x, y }.
 * @returns {Array<number>} The new rotated points.
 */
export const rotatePoints = (points, angle, center) => {
    const angleRad = (angle * Math.PI) / 180;
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);
    const newPoints = [];

    for (let i = 0; i < points.length; i += 2) {
        const x = points[i] - center.x;
        const y = points[i + 1] - center.y;

        newPoints.push(
            x * cos - y * sin + center.x,
            x * sin + y * cos + center.y
        );
    }
    return newPoints;
};

/**
 * Scales a flat array of points from a center point.
 * @param {Array<number>} points - The points to scale.
 * @param {number} scaleFactor - The factor to scale by (e.g., 1.2 for 20% larger).
 * @param {Object} center - The center of scaling with { x, y }.
 * @returns {Array<number>} The new scaled points.
 */
export const scalePoints = (points, scaleFactor, center) => {
    const newPoints = [];
    for (let i = 0; i < points.length; i += 2) {
        newPoints.push(
            center.x + (points[i] - center.x) * scaleFactor,
            center.y + (points[i + 1] - center.y) * scaleFactor
        );
    }
    return newPoints;
};

/**
 * Reflects a flat array of points across a vertical axis passing through the center.
 * @param {Array<number>} points - The points to reflect.
 * @param {Object} center - The center defining the vertical axis of reflection.
 * @returns {Array<number>} The new reflected points.
 */
export const reflectPoints = (points, center) => {
    const newPoints = [];
    for (let i = 0; i < points.length; i += 2) {
        newPoints.push(
            center.x - (points[i] - center.x),
            points[i + 1]
        );
    }
    return newPoints;
};

/**
 * Reflects a flat array of points across an arbitrary line defined by two points.
 * @param {Array<number>} points - The points to reflect [x1, y1, x2, y2, ...].
 * @param {Object} lineP1 - First point on the axis line { x, y }.
 * @param {Object} lineP2 - Second point on the axis line { x, y }.
 * @returns {Array<number>} The new reflected points.
 */
export const reflectPointsAcrossLine = (points, lineP1, lineP2) => {
    const dx = lineP2.x - lineP1.x;
    const dy = lineP2.y - lineP1.y;
    const lenSq = dx * dx + dy * dy;

    if (lenSq === 0) return [...points];

    const newPoints = [];
    for (let i = 0; i < points.length; i += 2) {
        const px = points[i] - lineP1.x;
        const py = points[i + 1] - lineP1.y;
        const t = (px * dx + py * dy) / lenSq;
        newPoints.push(
            2 * t * dx - px + lineP1.x,
            2 * t * dy - py + lineP1.y
        );
    }
    return newPoints;
};
